import { useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { decodePolyline } from '../utils/polyline';

// Fix para los iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Iconos personalizados para inicio y fin
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const collectionIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Componente para ajustar el mapa a los bounds
function MapBounds({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);

  return null;
}

export default function ItinerarioMap({ itinerario, steps, recolecciones }) {
  // Decodificar la geometría del recorrido
  const routeCoordinates = itinerario?.Recorrido
    ? decodePolyline(itinerario.Recorrido)
    : [];

  // Filtrar steps por tipo
  const startStep = steps?.find(s => s.Type === 'start');
  const endStep = steps?.find(s => s.Type === 'end');
  const jobSteps = steps?.filter(s => s.Type === 'job') || [];

  // Calcular bounds para centrar el mapa
  const allPoints = [];

  if (startStep) {
    allPoints.push([startStep.Latitud, startStep.Longitud]);
  }
  if (endStep) {
    allPoints.push([endStep.Latitud, endStep.Longitud]);
  }
  jobSteps.forEach(step => {
    if (step.Latitud && step.Longitud) {
      allPoints.push([step.Latitud, step.Longitud]);
    }
  });

  // Centro por defecto (Córdoba, Argentina)
  const defaultCenter = [-31.4201, -64.1888];
  const center = allPoints.length > 0 ? allPoints[0] : defaultCenter;

  if (!itinerario) {
    return (
      <div style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
        <p>No hay datos del itinerario para mostrar</p>
      </div>
    );
  }

  return (
    <div style={{ height: '500px', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Ajustar mapa a los bounds */}
        {allPoints.length > 0 && <MapBounds bounds={allPoints} />}

        {/* Ruta del recorrido */}
        {routeCoordinates.length > 0 && (
          <Polyline
            positions={routeCoordinates}
            color="#2196F3"
            weight={4}
            opacity={0.7}
          />
        )}

        {/* Marcador de inicio */}
        {startStep && (
          <Marker
            position={[startStep.Latitud, startStep.Longitud]}
            icon={startIcon}
          >
            <Popup>
              <strong>Inicio del Recorrido</strong><br />
              Orden: {startStep.Orden}
            </Popup>
          </Marker>
        )}

        {/* Marcador de fin */}
        {endStep && (
          <Marker
            position={[endStep.Latitud, endStep.Longitud]}
            icon={endIcon}
          >
            <Popup>
              <strong>Fin del Recorrido</strong><br />
              Orden: {endStep.Orden}
            </Popup>
          </Marker>
        )}

        {/* Marcadores de puntos de recolección */}
        {jobSteps.map((step, index) => {
          // Buscar la recolección correspondiente
          const recoleccion = recolecciones?.find(r => r.Orden === step.Orden);

          return (
            <Marker
              key={step.Id || index}
              position={[step.Latitud, step.Longitud]}
              icon={collectionIcon}
            >
              <Popup>
                <strong>Recolección #{step.Orden}</strong><br />
                {recoleccion && (
                  <>
                    Email: {recoleccion.Email}<br />
                    Dirección: {recoleccion.Direccion || 'N/A'}<br />
                    Volumen: {recoleccion.Volumen} L<br />
                    Estado: {recoleccion.FechaRetiro ? 'Retirado' : recoleccion.FechaAnulado ? 'Anulado' : 'Pendiente'}
                  </>
                )}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
